import { gsap } from 'gsap';
import scrollToY from '@util/scroll-y';
import offset from '@util/offset';

interface OptionConfig {
    text: string;
    value: string;
    selected?: boolean;
    disabled?: boolean;
}

interface CustomSelectConfig {
    id: string;
    name: string;
    type: string;
    value: string;
}

class CustomSelect extends HTMLElement implements CustomSelectConfig {
    /**
     * Variables by type
     * @private
     */
    name: string;
    type: string;
    value: string;
    #shadow: ShadowRoot;
    #wrapper: HTMLDivElement;
    #selectWrapper: HTMLDivElement;
    #selectContainer: HTMLUListElement;
    #valueText: HTMLSpanElement;
    #displayValue: HTMLDivElement;
    #isOpen: boolean;
    #position: string;
    #minHeight: number;
    #heightContainer: number;
    #heightViewport: number;
    #posContainer: number;
    #posView: number;
    #hiddenInput: HTMLInputElement;
    #heightInputFromCSS: number;

    constructor() {
        super();

        this.#isOpen = false;
        this.#position = 'down';

        this.shadowDOM();
        this.appendStyles();
        this.appendElements();
        this.setType();
    }

    shadowDOM() {
        this.#shadow = this.attachShadow({ mode: 'open' });
    }

    createStyles() {
        const stylesheet = document.createElement('link');
        stylesheet.setAttribute('rel', 'stylesheet');
        stylesheet.setAttribute('href', '/dist/styles/custom-select.css');

        return stylesheet;
    }

    createWrapper() {
        this.#wrapper = document.createElement('div');
        this.#wrapper.setAttribute('class', 'custom-select-wrapper');
    }

    createSelectWrapper() {
        this.#selectWrapper = document.createElement('div');
        this.#selectWrapper.setAttribute('class', 'select-wrapper');
    }

    createSelectContainer() {
        this.#selectContainer = document.createElement('ul');
        this.#selectContainer.setAttribute('class', 'select-container');
    }

    createValueText() {
        this.#valueText = document.createElement('span');
        this.#valueText.setAttribute('class', 'value-text');
    }

    createHiddenInput() {
        this.#hiddenInput = document.createElement('input');
        this.#hiddenInput.setAttribute('id', 'selectedValue');
        this.#hiddenInput.setAttribute('type', 'hidden');
        this.#hiddenInput.setAttribute('name', this.name);
    }

    createDisplayValue() {
        this.#displayValue = document.createElement('div');
        this.#displayValue.setAttribute('class', 'display-value');
    }

    createElements() {
        this.createWrapper();
        this.createSelectWrapper();
        this.createSelectContainer();
        this.createValueText();
        this.createHiddenInput();
        this.createDisplayValue();
    }

    appendStyles() {
        this.#shadow.appendChild(this.createStyles());
    }

    appendElements() {
        this.createElements();
        this.#shadow.appendChild(this.#wrapper);
        this.#wrapper.appendChild(this.#hiddenInput);
        this.#wrapper.appendChild(this.#displayValue);
        this.#displayValue.appendChild(this.#valueText);
        this.#selectWrapper.appendChild(this.#selectContainer);
        this.#wrapper.appendChild(this.#selectWrapper);
    }

    eventEmitter() {
        this.#selectWrapper.addEventListener('onblur', this.toggleSelf.bind(this));
        if (!this.#wrapper.classList.contains('disabled')) {
            this.#displayValue.addEventListener('click', this.toggleSelf.bind(this));
        }
    }

    #getCSSVar = (varName: string) => getComputedStyle(document.documentElement).getPropertyValue(`--${varName}`)

    getValues() {
        this.#heightInputFromCSS = (this.#getCSSVar('height-input')) ? parseInt(this.#getCSSVar('height-input'), 10) : 0;
        this.#minHeight = (this.#heightInputFromCSS * 4);
        this.#heightContainer = this.#heightInputFromCSS > 0 ? this.#selectContainer.offsetHeight : this.#selectContainer.offsetHeight;
        this.#heightViewport = document.documentElement.clientHeight;
        this.#posContainer = (Math.round(offset(this.#selectContainer).top));
        this.#posView = document.documentElement.scrollTop;
    }

    get height() {
        let setHeight = this.#heightContainer;
        let calculateStatement = (this.#heightContainer - (this.#posView - this.#posContainer)) > this.#heightViewport;

        if (this.#position === 'up' && (this.#posContainer - this.#heightContainer > this.#posView - this.#heightViewport)) {
            calculateStatement = (this.#posContainer - this.#heightContainer > this.#posView - this.#heightViewport);
        }

        if (calculateStatement) {
            const maxHeight = this.#heightViewport - (this.#posContainer - this.#posView) - 10;
            const options = this.#selectContainer.querySelectorAll('.select-option');
            let optionsHeight = this.#heightInputFromCSS;
            for (let o = 0; o < options.length; o += 1) {
                if (optionsHeight >= maxHeight) {
                    break;
                }

                if (optionsHeight < maxHeight) {
                    optionsHeight += (options[o] as HTMLElement).offsetHeight;
                }
            }

            setHeight = this.#minHeight;
            if (optionsHeight > this.#minHeight) {
                setHeight = optionsHeight;
            }
        }

        return `${setHeight}px`;
    }

    setValue(val) {
        this.value = val;
    }

    setType() {
        this.type = 'custom-select';
    }

    setPosition() {
        this.getValues();

        if (this.#posContainer + this.#minHeight > this.#heightViewport + this.#posView) {
            this.#position = 'up';
            this.#selectWrapper.classList.add('select-wrapper--up');
        } else if (this.#selectWrapper.classList.contains('select-wrapper--up')) {
            this.#selectWrapper.classList.remove('select-wrapper--up');
            this.#position = 'down';
        }
    }

    clickOutsideElement() {
        document.addEventListener('click', (event: Event & { target: HTMLButtonElement }) => {
            if (!this.contains(event.target) && this.#isOpen) {
                this.close();
            }
        });
    }

    open() {
        this.#isOpen = true;
        this.#selectWrapper.focus();

        this.setPosition();

        gsap.to(
            this.#selectWrapper,
            {
                duration: 0.25,
                ease: 'linear',
                height: this.height,
                onStart: () => {
                    this.#wrapper.classList.add('focused');
                    this.clickOutsideElement();
                },
            },
        );
    }

    close() {
        this.#isOpen = false;
        this.#selectWrapper.blur();
        gsap.to(
            this.#selectWrapper,
            {
                duration: 0.25,
                ease: 'linear',
                height: 0,
                onComplete: () => {
                    this.#wrapper.classList.remove('focused');
                },
            },
        );
    }

    toggleSelf() {
        if (!this.#isOpen) {
            this.open();
        } else {
            this.close();
        }
    }

    selectedValueIsValidUrl(val) {
        return (val.indexOf('//') !== -1);
    }

    selectedValueIsAnchor(val) {
        const navbar = document.querySelector('.navbar') as HTMLElement;
        const pagePart = document.getElementById(val.replace('#', ''));
        let gap = 25;

        if (navbar) {
            gap += navbar.offsetHeight;
        }

        scrollToY((offset(pagePart).top - gap), 1000, 'easeOutSine');
    }

    selectedValue(opt) {
        this.#valueText.innerHTML = opt.text;
        this.#hiddenInput.value = opt.value;
        this.setValue(opt.value);
    }

    selected(opt) {
        this.selectedValue(opt);
        this.toggleSelf();

        if (Number.isNaN(Number(opt.value))) {
            if (this.selectedValueIsValidUrl(opt.value)) {
                const windowTarget = opt.value.includes(window.location.origin) ? '_self' : '_blank';
                window.open(opt.value, windowTarget);
            } else if (opt.value.indexOf('#') !== -1) {
                this.selectedValueIsAnchor(opt.value);
            }
        }
    }

    connectedCallback() {
        if (this.hasAttribute('id')) {
            this.id = this.getAttribute('id');
        }

        if (this.hasAttribute('name')) {
            this.name = this.getAttribute('name');
            this.#hiddenInput.setAttribute('name', this.name);
        }

        if (this.hasAttribute('options')) {
            const options = JSON.parse(this.getAttribute('options'));

            if (Object.keys(options).length === 1) {
                options[0].selected = true;
                this.#wrapper.classList.add('disabled');
            }

            Object.values(options).forEach((option) => {
                const { text, value, selected } = option as unknown as OptionConfig;
                const optionElement = document.createElement('li');
                optionElement.setAttribute('class', 'select-option');
                optionElement.innerHTML = text;

                optionElement.addEventListener('click', () => {
                    const optionElements = this.#selectContainer.getElementsByClassName('selected');
                    while (optionElements.length > 0) {
                        optionElements[0].classList.remove('selected');
                    }

                    optionElement.classList.add('selected');
                    this.selected({ text, value });
                    this.dispatchEvent(new Event('change'));
                });

                if (selected) {
                    optionElement.classList.add('selected');
                    this.selectedValue({text, value});
                }

                this.#selectContainer.appendChild(optionElement);
            });

            this.eventEmitter();
        }
    }
}

customElements.define('custom-select', CustomSelect);
